"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.httpClient2 = void 0;
const errors_1 = require("./errors");
const fetchRetry_1 = __importStar(require("./fetchRetry"));
const getCommonRequestHeaders_1 = require("./getCommonRequestHeaders");
const helpers_1 = require("./helpers");
/**
 * # httpClient2
 *
 * Call our APIs
 *
 * @example
 *
 * GET request
 * ```ts
 * const user = await httpClient2.get("/api/user").json<User>()
 * ```
 *
 * @example
 *
 * POST request
 * ```ts
 * const message = await httpClient2.post("/api/send", {
 *     json: { text: "Thanks again!" },
 * }).json<Message>();
 * ```
 *
 * ## Errors:
 *
 * @throws HttpError - This exception is thrown if the server responds with a
 * 4xx or 5xx status code
 *
 * @throws NetworkError - This exception is thrown if the client could not reach
 * the server
 *
 * @throws JsonParseError - This exception is thrown if the client tries to
 * parse a response into JSON when the response body is not valid JSON
 *
 * ## Request headers:
 *
 * Common request headers are added to every request. You can add additional
 * headers or override default headers when making a request.
 *
 * @example
 *
 * ```ts
 * await httpClient2.get("/api/user", { headers: { foo: "Bar" } });
 * ```
 *
 * ## Fetch retries
 *
 * It will retry requests that fail due to a network error up-to 3 times with a
 * 1000ms delay between each attempt. The purpose of this is to make the product
 * feel more stable for users with a patchy internet connection.
 *
 * ## Configure
 *
 * You can configure `httpClient2` with a logger that receives the for the following events:
 * - `onRequestStart` when a request starts
 * - `onSuccessResponse` when a 2xx response is received
 * - `onErrorResponse` when an HttpError or NetworkError (after 3 attepts) occurs
 * - `onFetchRetry` when retrying after a network error occurs
 * - `onClientError` when some other error occurs
 *
 * You can also pass configure a general response handler. You may want to use this to setup
 * some global behaviour for API responses.
 *
 * @example
 *
 * ```ts
 * httpClient2.configure({
 *   logger: {
 *     onRequestStart: ({ request, init }) => Log.info("Request started"),
 *     onSuccessResponse: ({ request, response, init }) => Log.info("Request successful"),
 *     onHttpError: ({ request, error, init }) => Log.info("Request successful"),
 *   },
 *   onResponse: ({ request, response, init }) => {}
 * });
 * ```
 *
 * ## Controlling HttpError logging
 *
 * By default HttpErrors will be sent to the `onHttpError` logger function. However, you can opt out of this when making a request.
 *
 * ```ts
 * httpClient2.get({
 *   path: "/api/user",
 *   // Override the default logging behaviour
 *   logHttpError: ({ error, init, logError }) => {
 *     if (error.statusCode === 402) {
 *       // handle a special case
 *       Log.error("the user hasn't paid their subscription");
 *     } else {
 *       // if you want to fallback to the default logging you can
 *       logError();
 *     }
 *   }
 * });
 * ```
 */
class HttpClient2 {
    constructor() {
        this.logger = helpers_1.noopLogger;
        // eslint-disable-next-line @typescript-eslint/no-empty-function -- this is updated by the configure method
        this.onResponse = () => { };
        /**
         * Perform a HTTP GET request
         */
        this.get = this.createMethod({
            method: "GET",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Cache: "no-cache",
            },
        });
        /**
         * Perform a HTTP POST request
         */
        this.post = this.createMethod({
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Cache: "no-cache",
            },
        });
        /**
         * Perform a HTTP PUT request
         */
        this.put = this.createMethod({
            method: "PUT",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Cache: "no-cache",
            },
        });
        /**
         * Perform a HTTP PATCH request
         */
        this.patch = this.createMethod({
            method: "PATCH",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Cache: "no-cache",
            },
        });
        /**
         * Perform a HTTP DELETE request
         */
        this.delete = this.createMethod({
            method: "DELETE",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                Pragma: "no-cache",
                Cache: "no-cache",
            },
        });
    }
    createMethod(methodInit) {
        return (init) => {
            const result = (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    /**
                     * Combine three RequestInit objects to form the full request:
                     * 1. The top-level request details hardcoded into the method
                     *    definition.
                     * 2. Our own RequestInit that converts any JSON provided into a
                     *    response body string and also generates some common headers
                     *    that we send with every request.
                     * 3. Any other request details that are passed. These will take
                     *    precent over the above two.
                     */
                    const { json } = init, requestInit = __rest(init, ["json"]);
                    const combinedRequestInit = (0, helpers_1.combineInits)([
                        methodInit,
                        { headers: (0, getCommonRequestHeaders_1.getCommonRequestHeaders)() },
                        requestInit,
                    ]);
                    /**
                     * If JSON has been passed then stringify it. If the JSON cannot
                     * be serialized then throw JsonStringifyError and emit to the
                     * onJsonStringifyError callback
                     */
                    if (json) {
                        try {
                            combinedRequestInit.body = JSON.stringify(json);
                        }
                        catch (e) {
                            throw new errors_1.JsonStringifyError(new Request((0, helpers_1.buildPath)(init), combinedRequestInit), // we don't have a Request instance yet but build one to attach to this error
                            json, e);
                        }
                    }
                    /**
                     * Build the request
                     */
                    const request = new Request((0, helpers_1.buildPath)(init), combinedRequestInit);
                    yield this.logger.onRequestStart({
                        init: init,
                        request,
                    });
                    const retryOptions = (0, fetchRetry_1.getRetryOptions)(init, request, this.logger.onFetchRetry);
                    /**
                     * Make the request
                     */
                    let response;
                    try {
                        response = yield (0, fetchRetry_1.default)(request, retryOptions);
                    }
                    catch (e) {
                        throw new errors_1.NetworkError(request, e);
                    }
                    /**
                     * Emit to the onResponse handler
                     */
                    yield this.onResponse({
                        request,
                        response,
                        init: init,
                    });
                    /**
                     * Convert non `2xx` responses into an HttpError
                     */
                    if (!response.ok) {
                        const responseText = yield (0, helpers_1.getResponseBodyAsString)(response);
                        throw new errors_1.HttpError(responseText, request, response.clone());
                    }
                    yield this.logger.onSuccessResponse({
                        init: init,
                        request,
                        response: response.clone(),
                    });
                    /**
                     * Enhance the `.json()` method of the response so that it:
                     * 1. Accepts an optional generic type as the response
                     * 2. Emits to the onJsonParseError callback when the
                     *    response cannot be parsed to JSON. This allows us to
                     *    catch cases where the client is expecting JSON but the
                     *    server unexpectedly returns HTML.
                     */
                    const decoratedResponse = response.clone();
                    decoratedResponse.json = () => __awaiter(this, void 0, void 0, function* () {
                        try {
                            return (yield response.json());
                        }
                        catch (e) {
                            const error = new errors_1.JsonParseError(request, response, e);
                            yield this.logger.onClientError({
                                init: init,
                                error,
                            });
                            throw error;
                        }
                    });
                    return decoratedResponse;
                }
                catch (error) {
                    /**
                     * Emit and rethrow HttpErrors
                     */
                    if (error instanceof errors_1.HttpError && init.logHttpError) {
                        // If an override function was passed when making the
                        // request use that to log Http Errors instead of the
                        // configured logger. This lets you opt out of the
                        // default logging behaviour to handle specific endpoint
                        // behaviour.
                        yield init.logHttpError({
                            init: init,
                            error,
                            logError: () => this.logger.onHttpError({
                                init: init,
                                error,
                            }),
                        });
                    }
                    else if (error instanceof errors_1.HttpError) {
                        yield this.logger.onHttpError({
                            init: init,
                            error,
                        });
                    }
                    else if (error instanceof errors_1.NetworkError) {
                        yield this.logger.onNetworkError({
                            init: init,
                            error,
                        });
                    }
                    else {
                        yield this.logger.onClientError({
                            init: init,
                            error,
                        });
                    }
                    throw error;
                }
            }))();
            /**
             * Decorate the response with a .json() method so you can call it
             * directly on the response promise.
             *
             * @example
             * const user = get("/api/user").json<User>();
             */
            result.json = () => __awaiter(this, void 0, void 0, function* () {
                const response = yield result;
                return yield response.json();
            });
            return result;
        };
    }
    /**
     * Configure the client with a logger and on onResponse handler
     */
    configure(config) {
        var _a;
        this.logger = Object.assign(Object.assign({}, helpers_1.noopLogger), config.logger);
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.onResponse = (_a = config.onResponse) !== null && _a !== void 0 ? _a : (() => { });
    }
}
exports.httpClient2 = new HttpClient2();
//# sourceMappingURL=httpClient2.js.map